// ──────────────────────────────────────────────────────────────────────────
// CLIENT ANALYTICS
//
// Typed helpers for tracking the giga.com visitor → waitlist signup funnel.
// The app talks to this file, and this file talks to the analytics provider.
//
// Funnel:
//   Page visit → CTA click → Signup page → Modal open → Email submit → Done
// ──────────────────────────────────────────────────────────────────────────

declare global {
  interface Window {
    dataLayer: Record<string, unknown>[];
    amplitude?: any;
  }
}

// Amplitude is loaded from its CDN at runtime rather than bundled, so the
// build never has to resolve the SDK and the marketing site still works if
// the script is blocked. Every call below no-ops until the script lands.
const AMPLITUDE_SCRIPT = 'https://cdn.amplitude.com/script/{key}.js';
function amp() {
  return typeof window === 'undefined' ? undefined : window.amplitude;
}
function loadAmplitude(apiKey: string, onReady: () => void) {
  if (typeof document === 'undefined') return;
  if (amp()) {
    onReady();
    return;
  }
  const script = document.createElement('script');
  script.async = true;
  script.src = AMPLITUDE_SCRIPT.replace('{key}', apiKey);
  script.onload = onReady;
  script.onerror = () => console.warn('[analytics] Amplitude failed to load');
  document.head.appendChild(script);
}
type EventParams = Record<string, string | number | boolean | undefined>;
type UserProperties = Record<string, string | number | boolean | undefined>;
let analyticsInitialized = false;
let visitorIdentificationInitialized = false;
const ANON_ID_KEY = 'ggid';
const LEGACY_ANON_ID_KEY = 'giga_anon_id';
const ANON_ID_PREFIX = 'giga_';
const RB2B_KEY = '4N210HX5RV6Z';

// ── Event names ────────────────────────────────────────────────────────────
// Keep these as a const object so consuming code gets autocomplete + type safety.
export const AnalyticsEvent = {
  SIGNUP_CLICKED: 'Clicked Sign Up',
  WAITLIST_MODAL_OPENED: 'waitlist_modal_opened',
  WAITLIST_SUBMITTED: 'waitlist_submitted',
  WAITLIST_COMPLETED: 'waitlist_completed',
  WAITLIST_ERROR: 'waitlist_error'
} as const;
export type AnalyticsEventType = (typeof AnalyticsEvent)[keyof typeof AnalyticsEvent];

// ── Core send function ─────────────────────────────────────────────────────

export function getAnonymousId() {
  if (typeof window === 'undefined') return undefined;
  const fromUrl = new URLSearchParams(window.location.search).get(ANON_ID_KEY);
  const fromCookie = getCookie(ANON_ID_KEY);
  const fromLocalStorage = safeLocalStorageGet(ANON_ID_KEY);
  const fromLegacy = new URLSearchParams(window.location.search).get(LEGACY_ANON_ID_KEY) || getCookie(LEGACY_ANON_ID_KEY) || safeLocalStorageGet(LEGACY_ANON_ID_KEY);
  const existing = fromUrl || fromCookie || fromLocalStorage || fromLegacy;
  if (existing) {
    persistAnonymousId(existing);
    console.info('[analytics] using anonymous id', {
      anonymousId: existing,
      source: fromUrl ? 'url' : fromCookie ? 'cookie' : fromLocalStorage ? 'localStorage' : 'legacy'
    });
    return existing;
  }
  const created = createAnonymousId();
  persistAnonymousId(created);
  console.info('[analytics] created anonymous id', {
    anonymousId: created
  });
  return created;
}

/** Initialize client analytics once during the app lifecycle. */
export function initializeAnalytics() {
  if (analyticsInitialized || typeof window === 'undefined') return;
  if (!isProductionMarketingHost()) {
    console.info('[analytics] disabled on non-production host', {
      host: window.location.hostname
    });
    return;
  }
  const env = (import.meta as any)?.env ?? {};
  const apiKey = env.VITE_AMPLITUDE_API_KEY || '8c747d2664eeda746e587d2ea6bda003';
  const anonymousId = getAnonymousId();
  if (!apiKey) {
    console.warn('Amplitude API key missing — analytics disabled');
    return;
  }
  analyticsInitialized = true;
  loadAmplitude(apiKey, () => {
    const sdk = amp();
    if (!sdk) return;
    sdk.init?.(apiKey, {
      autocapture: true
    });
    if (anonymousId) sdk.setDeviceId?.(anonymousId);
    console.info('[analytics] initialized Amplitude', {
      deviceId: anonymousId
    });
  });
  initializeVisitorIdentification();
}
export function initializeVisitorIdentification() {
  if (visitorIdentificationInitialized || typeof window === 'undefined') return;
  if (!isProductionMarketingHost()) return;
  visitorIdentificationInitialized = true;
  if ((window as any).reb2b) {
    console.info('[analytics] RB2B already initialized');
    return;
  }
  (window as any).reb2b = {
    loaded: true
  };
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://ddwl4m2hdecbv.cloudfront.net/b/${RB2B_KEY}/${RB2B_KEY}.js.gz`;
  script.onload = () => console.info('[analytics] RB2B loaded');
  script.onerror = () => console.warn('[analytics] RB2B failed to load');
  const firstScript = document.getElementsByTagName('script')[0];
  firstScript?.parentNode?.insertBefore(script, firstScript);
  console.info('[analytics] initializing RB2B', {
    key: RB2B_KEY
  });
}

/** Fire an Amplitude event and push a matching GTM dataLayer event. */
export function trackEvent(eventName: string, params?: EventParams) {
  initializeAnalytics();
  const anonymousId = getAnonymousId();
  const eventParams = {
    ...params,
    [ANON_ID_KEY]: anonymousId
  };
  console.info('[analytics] track', {
    eventName,
    properties: eventParams
  });
  pushToDataLayer(eventName, eventParams);
  if (!isProductionMarketingHost()) return;
  amp()?.track?.(eventName, eventParams);
}

/** Associate future client-side events with a known user. */
export function identifyUser(userId: string, properties?: UserProperties) {
  initializeAnalytics();
  console.info('[analytics] identify', {
    userId,
    properties
  });
  pushToDataLayer('User Identified', {
    analytics_event_name: 'User Identified',
    user_id: userId,
    ...properties
  });
  if (!isProductionMarketingHost()) return;
  const sdk = amp();
  if (!sdk) return;
  sdk.setUserId?.(userId);
  if (properties && Object.keys(properties).length > 0 && sdk.Identify) {
    const identify = new sdk.Identify();
    Object.entries(properties).forEach(([key, value]) => {
      if (value !== undefined) identify.set(key, value);
    });
    sdk.identify?.(identify);
  }
}
export function withAnalyticsParams(href: string) {
  const anonymousId = getAnonymousId();
  if (!anonymousId) return href;
  try {
    const url = new URL(href, window.location.href);
    url.searchParams.set(ANON_ID_KEY, anonymousId);
    return url.toString();
  } catch {
    return href;
  }
}

// ── Funnel helpers ─────────────────────────────────────────────────────────

/** Someone clicked a signup CTA. `location` identifies which one (hero, nav,
 * closing, pricing-card, referral, etc.). */
export function trackSignupClick(location: string, ctaText?: string) {
  trackEvent(AnalyticsEvent.SIGNUP_CLICKED, {
    cta_location: location,
    cta_text: ctaText
  });
}
export const trackCtaClick = trackSignupClick;

/** The waitlist modal appeared on screen. */
export function trackWaitlistModalOpen(referredBy?: string | null) {
  trackEvent(AnalyticsEvent.WAITLIST_MODAL_OPENED, {
    was_referred: referredBy ? 'yes' : 'no'
  });
}

/** User submitted their email to the waitlist form. */
export function trackWaitlistSubmitted(email?: string) {
  trackEvent(AnalyticsEvent.WAITLIST_SUBMITTED, {
    email_domain: emailDomain(email)
  });
}

/** Waitlist API returned success. */
export function trackWaitlistCompleted(status: string, position?: number) {
  trackEvent(AnalyticsEvent.WAITLIST_COMPLETED, {
    signup_status: status,
    queue_position: position
  });
}

/** Waitlist API returned an error or timed out. */
export function trackWaitlistError(error?: string) {
  trackEvent(AnalyticsEvent.WAITLIST_ERROR, {
    error_message: error ? error.slice(0, 100) : 'unknown'
  });
}

/** User completed the account step and proceeded to checkout in the trial flow. */
export function trackTrialStarted(plan?: string) {
  trackEvent('trial_started', {
    plan
  });
}

/** User reached the checkout step (top of payment funnel). */
export function trackTrialCheckoutViewed(plan?: string) {
  trackEvent('trial_checkout_viewed', {
    plan
  });
}

/** User completed payment authorisation and entered the welcome step. */
export function trackTrialSubscribed(plan?: string) {
  trackEvent('trial_subscribed', {
    plan
  });
}

/** User confirmed a demo time slot. */
export function trackDemoBooked(date?: number, time?: string) {
  trackEvent('demo_booked', {
    selected_date: date,
    selected_time: time
  });
}

/** User clicked Accept and Continue on the Slack OAuth screen during signup. */
export function trackSignupSlackConnected() {
  trackEvent('signup_slack_connected', {});
}

/** User completed profile info (role, business type, use cases) during signup. */
export function trackSignupProfileCompleted(role?: string, businessType?: string) {
  trackEvent('signup_profile_completed', {
    role: role || undefined,
    business_type: businessType || undefined
  });
}

/** User selected a pricing tier on the pricing page. */
export function trackPricingPlanSelected(credits: number, price: number) {
  trackEvent('pricing_plan_selected', {
    credits,
    price
  });
}

// ── Helpers ────────────────────────────────────────────────────────────────

/** Extract just the domain from an email for privacy-safe reporting. */
function emailDomain(email?: string): string | undefined {
  if (!email) return undefined;
  const parts = email.split('@');
  return parts.length === 2 ? parts[1] : undefined;
}
function pushToDataLayer(eventName: string, properties: Record<string, unknown>) {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  const payload = {
    event: eventName,
    analytics_event_name: eventName,
    ...properties
  };
  window.dataLayer.push(payload);
  console.info('[analytics] dataLayer push', payload);
}
function createAnonymousId() {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return `${ANON_ID_PREFIX}${crypto.randomUUID().replace(/-/g, '')}`;
  }
  const random = Math.random().toString(36).slice(2);
  const now = Date.now().toString(36);
  return `${ANON_ID_PREFIX}${now}${random}`;
}
function persistAnonymousId(anonymousId: string) {
  safeLocalStorageSet(ANON_ID_KEY, anonymousId);
  setCookie(ANON_ID_KEY, anonymousId);
}
function safeLocalStorageGet(key: string) {
  try {
    return window.localStorage.getItem(key) || undefined;
  } catch {
    return undefined;
  }
}
function safeLocalStorageSet(key: string, value: string) {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Ignore storage failures; the shared cookie/query param still carries the id.
  }
}
function getCookie(name: string) {
  const target = `${encodeURIComponent(name)}=`;
  return document.cookie.split('; ').find(row => row.startsWith(target))?.slice(target.length);
}
function setCookie(name: string, value: string) {
  const parts = [`${encodeURIComponent(name)}=${encodeURIComponent(value)}`, 'Path=/', 'Max-Age=31536000', 'SameSite=Lax'];
  if (window.location.protocol === 'https:') parts.push('Secure');
  if (window.location.hostname === 'getgiga.com' || window.location.hostname.endsWith('.getgiga.com')) {
    parts.push('Domain=.getgiga.com');
  }
  document.cookie = parts.join('; ');
}
function isProductionMarketingHost() {
  return window.location.hostname === 'getgiga.com';
}